import { FC, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { StringKey } from '../../../../lang/index';
import { renderLinkedEventValue } from '../utils/render-linked-event-value.util';

export type QuickSignDocumentDetailsSummaryProps = {
  categoryLabel?: string;
  documentName?: string;
  expirationPeriodLabel?: ReactNode;
  linkedEventLabel?: string;
  showExpiration?: boolean;
  signatoryLines?: string[];
  signingModeLabel?: string;
  subCategoryLabel?: string;
};

const renderSignatoryLines = (signatoryLines: string[]) => (
  <ul className="list-none pl-0 text-left">
    {signatoryLines.map((line) => (
      <li className="text-xs text-gray-700" key={line}>
        {line}
      </li>
    ))}
  </ul>
);

const SummaryRow: FC<{
  titleId: StringKey;
  value?: ReactNode;
  valueClassName?: string;
}> = ({ titleId, value, valueClassName }) => (
  <SummaryItem
    className="py-3"
    title={<AppFormattedMessage id={titleId} />}
    titleClassName="w-fit"
    titleWrapperClassName="w-[120px]"
    value={value ?? '-'}
    valueClassName={valueClassName}
  />
);

export const QuickSignDocumentDetailsSummary: FC<QuickSignDocumentDetailsSummaryProps> = ({
  categoryLabel,
  documentName,
  expirationPeriodLabel,
  linkedEventLabel,
  showExpiration = false,
  signatoryLines = [],
  signingModeLabel,
  subCategoryLabel,
}) => {
  const linkedEventDisplay = linkedEventLabel?.trim() || '-';

  return (
    <>
      <section className="flex flex-col gap-2 rounded-lg bg-gray-50 pt-3">
        <span className="px-4 text-sm font-semibold text-gray-700">
          <AppFormattedMessage id="DOCUMENT" />
        </span>
        <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white px-4">
          <SummaryRow titleId="DOCUMENT_NAME" value={documentName} />
          <SummaryRow titleId="CATEGORY" value={categoryLabel} />
          <SummaryRow titleId="SUB_CATEGORY" value={subCategoryLabel} />
          {showExpiration && (
            <SummaryRow titleId="EXPIRATION_PERIOD" value={expirationPeriodLabel} />
          )}
          <SummaryRow
            titleId="LINKED_EVENT"
            value={renderLinkedEventValue(linkedEventDisplay)}
            valueClassName="min-w-0 max-w-[250px] overflow-hidden"
          />
        </div>
      </section>

      <div className="flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white px-4">
        <SummaryRow titleId="SIGNING_MODE" value={signingModeLabel} />
        <SummaryRow
          titleId="SIGNERS"
          value={signatoryLines.length > 0 ? renderSignatoryLines(signatoryLines) : '-'}
          valueClassName="max-w-none whitespace-normal"
        />
      </div>
    </>
  );
};
